import { useRef, useState } from 'react';
import {
  ClickAwayListener,
  Box,
  Typography,
  TextField,
  InputAdornment,
  Paper,
  Popper,
  MenuItem,
  useMediaQuery,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';

interface DropdownProps {
  options: string[];
  selectedOption?: string;
  onOptionSelect: (option: string) => void;
  fullWidth?: boolean;
  searchValue: string;
  searchHandler: (value: string) => void;
  children: React.ReactNode;
}

export default function Dropdown({
  options,
  selectedOption,
  onOptionSelect,
  fullWidth = false,
  searchValue,
  searchHandler,
  children,
}: DropdownProps) {
  const anchorRef = useRef<HTMLDivElement | null>(null);
  const [open, setOpen] = useState(false);
  const isMobile = useMediaQuery('(max-width:1300px)');

  const toggleDropdown = () => {
    setOpen((prev) => !prev);
  };

  const closeDropdown = () => {
    setOpen(false);
  };

  const handleOptionClick = (option: string) => {
    onOptionSelect(option);
    closeDropdown();
  };

  return (
    <Box display="inline-block" width={fullWidth ? '100%' : 'auto'}>
      <Box
        ref={anchorRef}
        sx={{
          cursor: 'pointer',
          width: 'fit-content',
          display: 'flex',
          alignItems: 'center',
        }}
        onClick={toggleDropdown}
      >
        {children}
        <Typography variant="body2" title={selectedOption}>
          {selectedOption && selectedOption.length > 20
            ? `${selectedOption.substring(0, 20)}...`
            : selectedOption}
        </Typography>
      </Box>
      <Popper
        open={open}
        anchorEl={anchorRef.current}
        placement="bottom-start"
        disablePortal
        sx={{ zIndex: 1000, width: isMobile ? '100%' : 'auto' }}
        modifiers={
          isMobile
            ? [] // mobile offset/remove
            : [
                {
                  name: 'offset',
                  options: {
                    offset: [-30, 8], // move further left
                  },
                },
              ]
        }
      >
        <ClickAwayListener onClickAway={closeDropdown}>
          <Paper
            sx={{
              width: isMobile ? 220 : 350,
              maxWidth: '100%',
              p: 2,
              ml: -2,
              position: 'relative',
              left: isMobile ? 0 : '-200px',
            }}
            elevation={3}
          >
            <TextField
              value={searchValue}
              onChange={(e) => searchHandler(e.target.value)}
              placeholder="Search Here"
              variant="outlined"
              size="small"
              fullWidth
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon color="action" />
                  </InputAdornment>
                ),
              }}
            />

            <Box
              mt={1}
              sx={{
                height: '200px',
                overflowY: 'scroll',
                scrollbarWidth: 'none', // Firefox
                msOverflowStyle: 'none', // IE / old Edge
                '&::-webkit-scrollbar': {
                  // Chrome / Safari / Edge (Chromium)
                  display: 'none',
                },
              }}
            >
              {options.map((option) => (
                <MenuItem
                  key={option}
                  sx={{ fontSize: '14px' }}
                  selected={option === selectedOption}
                  onClick={() => handleOptionClick(option)}
                >
                  {option}
                </MenuItem>
              ))}
            </Box>
          </Paper>
        </ClickAwayListener>
      </Popper>
    </Box>
  );
}
